import CantileverButton from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { formatDropdownString } from 'components/form/formHelpers'
import { isNil } from 'lodash'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { DamageCategoryOptions, GeneratedDamageCategory } from 'types'

import {
  AREA_THRESHOLD,
  consequenceMappings,
  probabilityDiscountFactorMapping,
} from './categorizationFunctions'

const StyledFlexRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 0.5rem;
`

const secondaryText = 'rgba(163, 163, 163, 1)'
const defaultText = 'rgba(229, 229, 229, 1)'

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(4rem, max-content) minmax(4rem, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-grow: 1;
  cursor: default;

  :nth-child(odd) {
    color: ${secondaryText};
  }

  :nth-child(even) {
    ${defaultText};
  }
`

interface CalculationLogicLabelProps {
  calculationTitle: string
  calculationString: string
  parameters: {
    [key: string]: number | string | undefined
  }
}

const CalculationLogicLabel: FC<CalculationLogicLabelProps> = ({
  calculationTitle,
  calculationString,
  parameters,
}) => {
  return (
    <div style={{ marginTop: '1rem' }}>
      <div>{calculationTitle}</div>
      <div style={{ margin: '0.2rem 0 0.2rem 0' }}>{calculationString}</div>
      {Object.entries(parameters).map(([key, value]) => {
        if (value === undefined) {
          return null
        }
        return (
          <div key={key}>
            {key}: {value}
          </div>
        )
      })}
    </div>
  )
}

interface DamageCategoryAutoGenCardProps {
  generatedDamageCategoryData: GeneratedDamageCategory
}

const formatDisplayNumbers = (value: number | undefined): number | string => {
  const formattedValue = value?.toPrecision(8)
  return formattedValue ? parseFloat(formattedValue) : ''
}

export const DamageCategoryAutoGenCard: FC<DamageCategoryAutoGenCardProps> = ({
  generatedDamageCategoryData,
}) => {
  const [inCollapsedView, setCollapsedView] = useState(true)

  const handleCollapsedViewChange = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation()
    setCollapsedView((prev) => !prev)
  }

  return (
    <CantileverCard.Group>
      <CantileverCard.Section>
        <CantileverCard.SectionHeader
          leadingContent={<span style={{ color: defaultText }}>Details</span>}
          trailingContent={
            <>
              <CantileverButton iconOnly>
                {inCollapsedView ? (
                  <ChevronDown
                    height={20}
                    width={20}
                    onClick={handleCollapsedViewChange}
                  />
                ) : (
                  <ChevronUp
                    height={20}
                    width={20}
                    onClick={handleCollapsedViewChange}
                  />
                )}
              </CantileverButton>
            </>
          }></CantileverCard.SectionHeader>
        {inCollapsedView ? null : (
          <div>
            <StyledFlexRow>
              <StyledGrid>
                <div>Category</div>
                <div>
                  {formatDropdownString(generatedDamageCategoryData.category) ??
                    DamageCategoryOptions.UNKNOWN}
                </div>
                <div>Probability</div>
                <div>
                  {formatDisplayNumbers(
                    generatedDamageCategoryData.probability
                  )}
                </div>
                {generatedDamageCategoryData.likelihood && (
                  <>
                    <div>Likelihood</div>
                    <div>
                      {formatDisplayNumbers(
                        generatedDamageCategoryData.likelihood
                      )}
                    </div>
                  </>
                )}
                <div>Consequence</div>
                <div>
                  {formatDisplayNumbers(
                    generatedDamageCategoryData.consequence
                  )}
                </div>
              </StyledGrid>
            </StyledFlexRow>
            <CalculationLogicLabel
              calculationTitle='Category'
              calculationString={`If the area is greater than ${AREA_THRESHOLD} sq ft, then the category is Rupture. Otherwise, the category is Pinhole Leak.`}
              parameters={{
                Area: `${generatedDamageCategoryData.area} sq ft`,
              }}
            />
            <CalculationLogicLabel
              calculationTitle='Probability'
              calculationString='The probability is calculated based on the percent of the damage in the indication less than tmin X discount factor'
              parameters={{
                'Percent Less than Tmin':
                  generatedDamageCategoryData.percentInRange,
                'Discount Factor': !isNil(generatedDamageCategoryData.category)
                  ? probabilityDiscountFactorMapping[
                      generatedDamageCategoryData.category
                    ]
                  : undefined,
              }}
            />
            <CalculationLogicLabel
              calculationTitle='Consequence'
              calculationString='The Consequence is calculated based on the ratio of the elevation of the indication to the height of the asset X category consequence'
              parameters={{
                'Elevation Ratio': generatedDamageCategoryData.elevationRatio,
                'Category Consequence': !isNil(
                  generatedDamageCategoryData.category
                )
                  ? consequenceMappings[generatedDamageCategoryData.category]
                  : undefined,
              }}
            />
          </div>
        )}
      </CantileverCard.Section>
    </CantileverCard.Group>
  )
}
